import React, { useState, useEffect, useRef } from 'react'
import { motion } from 'framer-motion'
import { 
  Image, 
  Upload, 
  Trash2, 
  Loader2,
  ImageIcon,
  VideoIcon,
  FileImage,
  FileVideo
} from 'lucide-react'
import toast from 'react-hot-toast'
import mediaService, { MediaCategory } from '../services/mediaService'
import apiService from '../services/api'
import ConfirmationDialog from '../components/ConfirmationDialog'
import { MEDIA_SECTIONS, validateImageFile, validateVideoFile, validateImageFiles, validateVideoFiles } from '../config/mediaValidation'

const Media: React.FC = () => {
  const [loading, setLoading] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [uploadProgress, setUploadProgress] = useState<{ [key: string]: number }>({})
  const [activeTab, setActiveTab] = useState<'images' | 'videos'>('images')
  const [media, setMedia] = useState<{
    images: { logo?: string; coverImage?: string; gallery: string[] }
    videos: { introVideo?: string; gallery: string[] }
  }>({
    images: { gallery: [] },
    videos: { gallery: [] }
  })
  const [mediaUrls, setMediaUrls] = useState<{
    images: { logo?: string; coverImage?: string; gallery: string[] }
    videos: { introVideo?: string; gallery: string[] }
  }>({
    images: { gallery: [] },
    videos: { gallery: [] }
  })
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const [itemToDelete, setItemToDelete] = useState<{ url: string; category: MediaCategory } | null>(null)
  const [deleting, setDeleting] = useState(false)

  const fileInputRefs = {
    logo: useRef<HTMLInputElement>(null),
    coverImage: useRef<HTMLInputElement>(null),
    gallery: useRef<HTMLInputElement>(null),
    introVideo: useRef<HTMLInputElement>(null),
    videoGallery: useRef<HTMLInputElement>(null)
  }

  useEffect(() => {
    loadMedia()
  }, [])

  const loadMedia = async () => {
    try {
      setLoading(true)
      const mediaData = await mediaService.getSchoolMedia()
      setMedia(mediaData)
      
      // Build display URLs from keys (domain + key)
      const getUrl = (key: string | undefined): string | undefined => {
        if (!key) return undefined
        return mediaService.getMediaUrlFromKey(key)
      }
      setMediaUrls({
        images: {
          logo: getUrl(mediaData.images.logo),
          coverImage: getUrl(mediaData.images.coverImage),
          gallery: (mediaData.images.gallery || []).map(key => getUrl(key) || key)
        },
        videos: {
          introVideo: getUrl(mediaData.videos.introVideo),
          gallery: (mediaData.videos.gallery || []).map(key => getUrl(key) || key)
        }
      })
    } catch (error: any) {
      toast.error(error.message || 'Failed to load media')
    } finally {
      setLoading(false)
    }
  }

  const categoryToSection: Partial<Record<MediaCategory, import('../config/mediaValidation').MediaSection>> = {
    logo: 'schoolLogo',
    coverImage: 'schoolCoverImage',
    gallery: 'schoolGallery',
    introVideo: 'schoolIntroVideo',
    videoGallery: 'schoolVideoGallery',
  }

  const handleFileSelect = async (
    file: File,
    category: MediaCategory
  ) => {
    const section = categoryToSection[category]
    const isImage = ['logo', 'coverImage', 'gallery'].includes(category)
    const currentCount = isImage ? (category === 'gallery' ? (media.images?.gallery?.length ?? 0) : 0) : (category === 'videoGallery' ? (media.videos?.gallery?.length ?? 0) : 0)
    const validation = section ? (isImage ? validateImageFile(file, section, currentCount) : validateVideoFile(file, section, currentCount)) : { valid: true }
    if (!validation.valid) {
      toast.error(validation.error || 'Invalid file')
      return
    }

    try {
      setUploading(true)
      const fileId = `${category}-${Date.now()}`
      setUploadProgress({ [fileId]: 0 })

      const result = await mediaService.uploadFile(
        file,
        category,
        undefined, // schoolId - will be determined by backend from auth
        undefined, // courseId
        (progress) => {
          setUploadProgress(prev => ({ ...prev, [fileId]: progress }))
        }
      )

      if (result.success) {
        // Update school profile with the fileKey
        try {
          const fileKey = result.data.fileKey
          const currentMedia = await mediaService.getSchoolMedia()
          
          // Ensure we have the correct structure
          console.log('Current media:', currentMedia)
          console.log('Category:', category)
          console.log('FileKey:', fileKey)
          
          const updateData: any = {}
          
          if (category === 'logo') {
            updateData.images = {
              logo: fileKey,
              coverImage: currentMedia.images?.coverImage,
              gallery: currentMedia.images?.gallery || []
            }
          } else if (category === 'coverImage') {
            updateData.images = {
              logo: currentMedia.images?.logo,
              coverImage: fileKey,
              gallery: currentMedia.images?.gallery || []
            }
          } else if (category === 'gallery') {
            // For gallery, we need to append to the array
            const currentGallery = currentMedia.images?.gallery || []
            updateData.images = {
              logo: currentMedia.images?.logo,
              coverImage: currentMedia.images?.coverImage,
              gallery: [...currentGallery, fileKey]
            }
          } else if (category === 'introVideo') {
            updateData.videos = {
              introVideo: fileKey,
              gallery: currentMedia.videos?.gallery || []
            }
          } else if (category === 'videoGallery') {
            const currentVideoGallery = currentMedia.videos?.gallery || []
            updateData.videos = {
              introVideo: currentMedia.videos?.introVideo,
              gallery: [...currentVideoGallery, fileKey]
            }
          }
          
          console.log('Update data being sent:', JSON.stringify(updateData, null, 2))
          
          // Update school profile
          await apiService.updateSchoolProfile(updateData)
        } catch (updateError) {
          console.error('Error updating school profile:', updateError)
          toast.error('File uploaded but failed to update profile')
        }
        
        toast.success('File uploaded successfully')
        await loadMedia()
        setUploadProgress(prev => {
          const newProgress = { ...prev }
          delete newProgress[fileId]
          return newProgress
        })
      }
    } catch (error: any) {
      toast.error(error.message || 'Failed to upload file')
    } finally {
      setUploading(false)
    }
  }

  const handleDeleteClick = (url: string, category: MediaCategory) => {
    setItemToDelete({ url, category })
    setShowDeleteDialog(true)
  }

  const handleDeleteConfirm = async () => {
    if (!itemToDelete) return

    try {
      setDeleting(true)
      const fileKey = mediaService.extractFileKeyFromUrl(itemToDelete.url)
      if (!fileKey) {
        toast.error('Could not extract file key from URL')
        setShowDeleteDialog(false)
        setItemToDelete(null)
        return
      }

      const result = await mediaService.deleteFile(fileKey, itemToDelete.category)
      if (result.success) {
        toast.success('Media deleted successfully')
        await loadMedia()
        setShowDeleteDialog(false)
        setItemToDelete(null)
      }
    } catch (error: any) {
      toast.error(error.message || 'Failed to delete media')
    } finally {
      setDeleting(false)
    }
  }

  const handleDeleteCancel = () => {
    setShowDeleteDialog(false)
    setItemToDelete(null)
  }

  const handleMultipleFiles = async (
    files: FileList | null,
    category: MediaCategory
  ) => {
    if (!files || files.length === 0) return

    const fileArray = Array.from(files)
    const section = (category === 'gallery' ? 'schoolGallery' : 'schoolVideoGallery') as import('../config/mediaValidation').MediaSection
    const currentCount = category === 'gallery' ? (media.images?.gallery?.length ?? 0) : (media.videos?.gallery?.length ?? 0)
    const validation = category === 'gallery'
      ? validateImageFiles(fileArray, section, currentCount)
      : validateVideoFiles(fileArray, section, currentCount)
    if (!validation.valid) {
      toast.error(validation.error || 'Validation failed')
      return
    }
    const validFiles = fileArray

    try {
      setUploading(true)
      const uploadedFileKeys: string[] = []
      
      for (const file of validFiles) {
        const fileId = `${category}-${Date.now()}-${file.name}`
        setUploadProgress(prev => ({ ...prev, [fileId]: 0 }))

        const result = await mediaService.uploadFile(
          file,
          category,
          undefined, // schoolId - will be determined by backend from auth
          undefined, // courseId
          (progress) => {
            setUploadProgress(prev => ({ ...prev, [fileId]: progress }))
          }
        )

        if (result.success && result.data.fileKey) {
          uploadedFileKeys.push(result.data.fileKey)
        }

        setUploadProgress(prev => {
          const newProgress = { ...prev }
          delete newProgress[fileId]
          return newProgress
        })
      }

      // Update school profile with fileKeys
      if (uploadedFileKeys.length > 0) {
        try {
          const currentMedia = await mediaService.getSchoolMedia()
          const updateData: any = {}
          
          if (category === 'gallery') {
            const currentGallery = currentMedia.images.gallery || []
            updateData.images = {
              ...currentMedia.images,
              gallery: [...currentGallery, ...uploadedFileKeys]
            }
          } else if (category === 'videoGallery') {
            const currentVideoGallery = currentMedia.videos.gallery || []
            updateData.videos = {
              ...currentMedia.videos,
              gallery: [...currentVideoGallery, ...uploadedFileKeys]
            }
          }
          
          if (Object.keys(updateData).length > 0) {
            await apiService.updateSchoolProfile(updateData)
          }
        } catch (updateError) {
          console.error('Error updating school profile:', updateError)
          toast.error('Files uploaded but failed to update profile')
        }
      }
      
      toast.success(`${validFiles.length} file(s) uploaded successfully`)
      await loadMedia()
    } catch (error: any) {
      toast.error(error.message || 'Failed to upload files')
    } finally {
      setUploading(false)
    }
  }

  const MediaCard = ({ url, category, onDelete }: { url: string; category: MediaCategory; onDelete: () => void }) => {
    const isImage = category === 'logo' || category === 'coverImage' || category === 'gallery'

    return (
      <div className="relative group bg-gray-100 rounded-lg overflow-hidden aspect-video">
        {isImage ? (
          <img
            src={url}
            alt={category}
            className="w-full h-full object-cover"
            crossOrigin="anonymous"
            onError={(e) => {
              const target = e.target as HTMLImageElement
              target.style.display = 'none'
            }}
          />
        ) : (
          <video
            src={url}
            className="w-full h-full object-cover"
            controls
            onError={(e) => {
              (e.target as HTMLVideoElement).style.display = 'none'
            }}
          />
        )}
        <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-50 transition-opacity flex items-center justify-center">
          <button
            onClick={onDelete}
            className="opacity-0 group-hover:opacity-100 transition-opacity bg-red-500 text-white p-2 rounded-full hover:bg-red-600"
          >
            <Trash2 className="h-5 w-5" />
          </button>
        </div>
      </div>
    )
  }

  const UploadSection = ({ 
    title, 
    description, 
    category, 
    accept, 
    multiple = false,
    currentUrl 
  }: { 
    title: string
    description: string
    category: MediaCategory
    accept: string
    multiple?: boolean
    currentUrl?: string
  }) => {
    const fileInputRef = fileInputRefs[category as keyof typeof fileInputRefs]
    const isImage = category === 'logo' || category === 'coverImage' || category === 'gallery'

    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
            <p className="text-sm text-gray-500 mt-1">{description}</p>
          </div>
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <Upload className="h-4 w-4" />
            {currentUrl ? 'Replace' : 'Upload'}
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept={accept}
            multiple={multiple}
            className="hidden"
            onChange={(e) => {
              const files = e.target.files
              if (files && files.length > 0) {
                if (multiple) {
                  handleMultipleFiles(files, category)
                } else {
                  handleFileSelect(files[0], category)
                }
              }
              // Reset input
              e.target.value = ''
            }}
          />
        </div>

        {currentUrl && (
          <div className="mt-4">
            <MediaCard
              url={currentUrl}
              category={category}
              onDelete={() => handleDeleteClick(currentUrl, category)}
            />
          </div>
        )}

        {!currentUrl && (
          <div className="mt-4 border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
            {isImage ? (
              <ImageIcon className="h-12 w-12 text-gray-400 mx-auto mb-2" />
            ) : (
              <VideoIcon className="h-12 w-12 text-gray-400 mx-auto mb-2" />
            )}
            <p className="text-gray-500">No {title.toLowerCase()} uploaded</p>
          </div>
        )}
      </div>
    )
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
      </div>
    )
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="space-y-6"
    >
      {/* Header */}
      <div className="relative overflow-hidden bg-gradient-to-r from-primary-500 via-primary-600 to-primary-700 shadow-2xl rounded-2xl">
        <div className="absolute inset-0 bg-gradient-to-r from-primary-600/20 via-primary-700/20 to-primary-800/20"></div>
        <div className="relative flex items-center justify-between p-4">
          <div className="flex items-center space-x-4">
            <div className="relative">
              <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center shadow-xl ring-2 ring-white/30">
                <Image className="h-6 w-6 text-white" />
              </div>
              <div className="absolute -top-1 -right-1 w-3 h-3 bg-gradient-to-r from-emerald-400 to-cyan-400 rounded-full border-2 border-white shadow-lg"></div>
            </div>
            <div>
              <h1 className="text-xl font-bold text-white">📸 Media Library</h1>
              <p className="text-white/90 text-sm">Upload and manage your school's media files</p>
            </div>
          </div>
          {uploading && (
            <div className="flex items-center gap-2 text-white bg-white/20 backdrop-blur-sm rounded-xl px-4 py-2 border border-white/30">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span className="text-sm font-medium">Uploading...</span>
            </div>
          )}
        </div>
      </div>
      
      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('images')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'images'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <div className="flex items-center gap-2">
              <FileImage className="h-5 w-5" />
              Images
            </div>
          </button>
          <button
            onClick={() => setActiveTab('videos')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'videos'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <div className="flex items-center gap-2">
              <FileVideo className="h-5 w-5" />
              Videos
            </div>
          </button>
        </nav>
      </div>

      {/* Images Tab */}
      {activeTab === 'images' && (
        <div className="space-y-6">
          <UploadSection
            title="School Logo"
            description="Upload your school logo (Recommended: 200x200px, PNG or JPG)"
            category="logo"
            accept={MEDIA_SECTIONS.schoolLogo.acceptImages!}
            currentUrl={mediaUrls.images.logo}
          />

          <UploadSection
            title="Cover Image"
            description="Upload a cover image for your school (Recommended: 1920x1080px, JPG or PNG)"
            category="coverImage"
            accept={MEDIA_SECTIONS.schoolCoverImage.acceptImages!}
            currentUrl={mediaUrls.images.coverImage}
          />

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Image Gallery</h3>
                <p className="text-sm text-gray-500 mt-1">Upload multiple images for your gallery</p>
              </div>
              <button
                onClick={() => fileInputRefs.gallery.current?.click()}
                disabled={uploading}
                className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <Upload className="h-4 w-4" />
                Add Images
              </button>
              <input
                ref={fileInputRefs.gallery}
                type="file"
                accept={MEDIA_SECTIONS.schoolGallery.acceptImages!}
                multiple
                className="hidden"
                onChange={(e) => {
                  handleMultipleFiles(e.target.files, 'gallery')
                  e.target.value = ''
                }}
              />
            </div>

            {mediaUrls.images.gallery.length > 0 ? (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mt-4">
                {mediaUrls.images.gallery.map((url, index) => (
                  <MediaCard
                    key={index}
                    url={url}
                    category="gallery"
                    onDelete={() => handleDeleteClick(media.images.gallery[index] || url, 'gallery')}
                  />
                ))}
              </div>
            ) : (
              <div className="mt-4 border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
                <ImageIcon className="h-12 w-12 text-gray-400 mx-auto mb-2" />
                <p className="text-gray-500">No gallery images uploaded</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Videos Tab */}
      {activeTab === 'videos' && (
        <div className="space-y-6">
          <UploadSection
            title="Intro Video"
            description="Upload an introduction video for your school (Max: 100MB, MP4, WebM, or QuickTime)"
            category="introVideo"
            accept={MEDIA_SECTIONS.schoolIntroVideo.acceptVideos!}
            currentUrl={mediaUrls.videos.introVideo}
          />

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Video Gallery</h3>
                <p className="text-sm text-gray-500 mt-1">Upload multiple videos for your gallery</p>
              </div>
              <button
                onClick={() => fileInputRefs.videoGallery.current?.click()}
                disabled={uploading}
                className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <Upload className="h-4 w-4" />
                Add Videos
              </button>
              <input
                ref={fileInputRefs.videoGallery}
                type="file"
                accept={MEDIA_SECTIONS.schoolVideoGallery.acceptVideos!}
                multiple
                className="hidden"
                onChange={(e) => {
                  handleMultipleFiles(e.target.files, 'videoGallery')
                  e.target.value = ''
                }}
              />
            </div>

            {mediaUrls.videos.gallery.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
                {mediaUrls.videos.gallery.map((url, index) => (
                  <MediaCard
                    key={index}
                    url={url}
                    category="videoGallery"
                    onDelete={() => handleDeleteClick(media.videos.gallery[index] || url, 'videoGallery')}
                  />
                ))}
              </div>
            ) : (
              <div className="mt-4 border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
                <VideoIcon className="h-12 w-12 text-gray-400 mx-auto mb-2" />
                <p className="text-gray-500">No gallery videos uploaded</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Upload Progress */}
      {Object.keys(uploadProgress).length > 0 && (
        <div className="fixed bottom-4 right-4 bg-white rounded-lg shadow-lg p-4 max-w-sm">
          <h4 className="font-semibold text-gray-900 mb-2">Upload Progress</h4>
          {Object.entries(uploadProgress).map(([fileId, progress]) => (
            <div key={fileId} className="mb-2">
              <div className="flex items-center justify-between text-sm text-gray-600 mb-1">
                <span className="truncate">{fileId}</span>
                <span>{progress}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-primary-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <ConfirmationDialog
        isOpen={showDeleteDialog}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title="Delete Media"
        message="Are you sure you want to delete this media? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        confirmButtonColor="red"
        isLoading={deleting}
      />
    </motion.div>
  )
}

export default Media
